import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, FileText, Download, Flag } from 'lucide-react';
import { usePermissions } from '@contexts/AuthContext';
import { contractService, importService } from '@services';
import { useDebounce } from '@hooks/useDebounce';
import { usePagination } from '@hooks/usePagination';
import { useColumnFilters } from '@hooks/useColumnFilters';
import { useContractFilterOptions } from '@hooks/useContractFilterOptions';
import { useTableSort } from '@hooks/useTableSort';
import { useContractPageData } from '@hooks/useContractPageData';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import ListPageHeader from '@components/common/ListPageHeader';
import PageLayout from '@components/layout/PageLayout';
import { FilterButtons } from '@components/common/FilterButtons';
import { RZTable, ColumnSelector } from '@components/common/table';
import { ContractFormModal, ReassessmentStatusCell } from '@components/features/contracts';
import { Contract, TableColumn } from '@app-types';
import { exportContracts } from '@utils/exportUtils';
import { CONTRACT_TEXT_FILTER_MAX_LENGTH } from '@utils/columnFilterUrl';
import { formatCalendarDate } from '@utils/dateUtils';
import { contractStatusToVariant } from '@utils/badgeMappers';
import { getApiErrorMessage } from '@utils/apiError';
import { showToast } from '@utils/toast';
import { CONTRACT_AMOUNT_LABEL } from '@utils/contractLabels';
import { useReferenceCatalog } from '@hooks/useReferenceCatalog';
import { getContractStatusLabel, getScopeLabel } from '@utils/referenceCatalog';
import { LoadingOverlay } from '@components/common/loading';
import ErrorState from '@components/common/ErrorState';
import styles from './ContractsPage.module.css';

// Contract already has contract_managers array in type definition

// Labels for the "no value" filter choice (ADO-1118, CONTEXT.md). Unassigned is
// about who or what a contract is assigned to; Blank is a plain missing value.
const UNASSIGNED = 'Unassigned';
const BLANK = 'Blank';
const BLANK_DATE = 'Blank (no date)';
const BLANK_AMOUNT = 'Blank (no amount)';

// Column configuration for the contracts table
// The reassessment column filterNode is computed dynamically inside the component.
// Columns without emptyFilterLabel can never be empty, so they offer no such choice.
const contractColumns: TableColumn[] = [
  { key: 'tier', label: 'Tier', defaultVisible: false, filterKey: 'tier', filterType: 'select', emptyFilterLabel: UNASSIGNED },
  { key: 'scope', label: 'Scope', defaultVisible: false, filterKey: 'scope', filterType: 'select' },
  { key: 'division', label: 'Division', defaultVisible: false, filterKey: 'division', filterType: 'select', emptyFilterLabel: UNASSIGNED },
  { key: 'supplier', label: 'Supplier Name', defaultVisible: true, filterKey: 'supplier', emptyFilterLabel: BLANK, maxFilterLength: CONTRACT_TEXT_FILTER_MAX_LENGTH },
  {
    key: 'contract_number',
    label: 'Contract Number',
    defaultVisible: true,
    alwaysVisible: true,
    filterKey: 'contract_number',
    maxFilterLength: CONTRACT_TEXT_FILTER_MAX_LENGTH
  },
  // Not sortable: owners are a to-many relation, which the API can only order by
  // count rather than by name, so a sortable header would mislead (ADO-1099).
  { key: 'owner', label: 'Contract Owner', defaultVisible: true, filterKey: 'contract_owner', sortable: false, emptyFilterLabel: UNASSIGNED, maxFilterLength: CONTRACT_TEXT_FILTER_MAX_LENGTH },
  { key: 'manager', label: 'Contract Manager', defaultVisible: true, filterKey: 'contract_manager', emptyFilterLabel: UNASSIGNED, maxFilterLength: CONTRACT_TEXT_FILTER_MAX_LENGTH },
  { key: 'name', label: 'Contract Name', defaultVisible: true, filterKey: 'name', maxFilterLength: CONTRACT_TEXT_FILTER_MAX_LENGTH },
  { key: 'contract_type', label: 'Contract Type', defaultVisible: true, filterKey: 'contract_type', filterType: 'select' },
  { key: 'start_date', label: 'Contract Start Date', defaultVisible: true, filterKey: 'start_date', filterType: 'dateRange' },
  { key: 'end_date', label: 'Contract End Date', defaultVisible: true, filterKey: 'end_date', filterType: 'dateRange', emptyFilterLabel: BLANK_DATE },
  // Key stays 'value' (persisted column sets and the export map key on it); the data field is `amount`.
  { key: 'value', label: CONTRACT_AMOUNT_LABEL, defaultVisible: true, filterKey: 'amount', filterType: 'numberRange', emptyFilterLabel: BLANK_AMOUNT },
  { key: 'cost_center', label: 'Cost Center', defaultVisible: false, filterKey: 'cost_center', emptyFilterLabel: BLANK, maxFilterLength: CONTRACT_TEXT_FILTER_MAX_LENGTH },
  { key: 'initial_assessment_date', label: 'Original Assessment Date', defaultVisible: false, filterKey: 'initial_assessment_date', filterType: 'dateRange', emptyFilterLabel: BLANK_DATE },
  { key: 'last_assessment_date', label: 'Date of Last Assessment', defaultVisible: false, filterKey: 'last_assessment_date', filterType: 'dateRange', emptyFilterLabel: BLANK_DATE },
  { key: 'target_date', label: 'Target Assessment Date', defaultVisible: false, filterKey: 'target_date', filterType: 'dateRange', emptyFilterLabel: BLANK_DATE },
  { key: 'status', label: 'Status', defaultVisible: true, filterKey: 'status', filterType: 'select' },
  { key: 'reassessment', label: 'Reassessment', defaultVisible: true, filterable: false, sortable: false }
];

const allStatusFilters = [
  { key: 'all', label: 'All' },
  { key: 'Active', label: 'Active' },
  { key: 'Inactive', label: 'Inactive' },
  { key: 'unassigned', label: 'Unassigned Manager' },
];

const ContractsPage: React.FC = () => {
  const permissions = usePermissions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  useReferenceCatalog();
  const catalogStatusFilters = allStatusFilters.map((filter) => ({
    ...filter,
    label:
      filter.key === 'all' || filter.key === 'unassigned'
        ? filter.label
        : getContractStatusLabel(filter.key) || filter.label,
  }));
  const canSeePendingScope = Boolean(permissions.isAdmin);

  const scopeSettingsQuery = useQuery({
    queryKey: ['scope-settings'],
    queryFn: () => importService.getScopeSettings(),
    enabled: canSeePendingScope
  });

  const isGlobalScopeOn = scopeSettingsQuery.data?.global_scope_calculation === true;

  const pendingScopeQuery = useQuery({
    queryKey: ['pending-scope', 'count'],
    queryFn: () => contractService.getPendingScopeContracts(),
    enabled: canSeePendingScope && isGlobalScopeOn
  });

  const pendingScopeCount = pendingScopeQuery.data?.total ?? 0;
  const showPendingScopeLink = canSeePendingScope && isGlobalScopeOn;

  // Admin/Program Lead keep the Unassigned Manager tab; scoped Contract Role does not (ADO-1153).
  const showUnassignedManager = !permissions.isContractRole || permissions.isAdmin;
  const statusFilters = showUnassignedManager
    ? catalogStatusFilters
    : catalogStatusFilters.filter((filter) => filter.key !== 'unassigned');

  // Initialize state from URL parameters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const initialStatus = searchParams.get('status') || 'all';
  const [statusFilter, setStatusFilter] = useState(
    initialStatus === 'unassigned' && !showUnassignedManager ? 'all' : initialStatus
  );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [reassessmentFilter, setReassessmentFilter] = useState<string | undefined>(undefined);

  // Pagination with localStorage persistence
  const { currentPage, pageSize, setCurrentPage, setPageSize, resetToFirstPage } = usePagination({
    storageKey: 'contracts-page-size',
    defaultPageSize: 25
  });

  // Debounced search query to avoid too many API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 500); // 500ms delay

  // Column filters with URL persistence and debounce
  const {
    filterValues: columnFilterValues,
    debouncedFilterValues: debouncedColumnFilters,
    setFilter: setColumnFilter,
    clearAllFilters: clearColumnFilters,
    hasActiveFilters: hasActiveColumnFilters,
    getApiParam: getColumnFiltersParam,
    droppedFilters,
    dismissDroppedFilters
  } = useColumnFilters({ columns: contractColumns });

  // Selectable values for the choice columns (Status, Scope, Division, Tier, Type)
  const filterOptions = useContractFilterOptions();

  /**
   * Clear every filter surface, not just the column filters (FR-007).
   *
   * The Contracts page has four: the column filter row, the search box, the
   * status button bar and the reassessment dropdown. Clearing only cf_* left
   * the list still filtered while the row claimed it was clear.
   */
  const clearAllFilters = useCallback(() => {
    clearColumnFilters();
    setSearchQuery('');
    setStatusFilter('all');
    setReassessmentFilter(undefined);
    resetToFirstPage();
  }, [clearColumnFilters, resetToFirstPage]);

  const hasAnyActiveFilter =
    hasActiveColumnFilters ||
    searchQuery !== '' ||
    statusFilter !== 'all' ||
    reassessmentFilter !== undefined;

  /**
   * Tell the user which filters a shared link carried that could not be used
   * (FR-015a). The page still loaded and the remaining filters still applied —
   * a single bad value must never be a locked door.
   */
  useEffect(() => {
    if (droppedFilters.length === 0) return;

    const describe = (key: string) =>
      (contractColumns.find((col) => col.key === key)?.label as string) ?? key;

    const summary = droppedFilters
      .map((dropped) => `${describe(dropped.columnKey)} ${dropped.reason}`)
      .join('; ');

    showToast.warning(`Some filters from the link were ignored: ${summary}`);
    dismissDroppedFilters();
  }, [droppedFilters, dismissDroppedFilters]);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    const stored = localStorage.getItem('contracts-visible-columns');
    if (stored) {
      return new Set(JSON.parse(stored));
    }
    // Default visible columns
    return new Set(contractColumns.filter((col) => col.defaultVisible).map((col) => col.key));
  });

  // Save column preferences to localStorage
  useEffect(() => {
    localStorage.setItem('contracts-visible-columns', JSON.stringify(Array.from(visibleColumns)));
  }, [visibleColumns]);

  // Toggle column visibility
  const toggleColumn = (columnKey: string) => {
    const column = contractColumns.find((col) => col.key === columnKey);
    if (column?.alwaysVisible) return; // Don't allow hiding always-visible columns

    setVisibleColumns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  // Check if a column should be visible
  const isColumnVisible = (columnKey: string): boolean => {
    return visibleColumns.has(columnKey);
  };

  // Get visible columns for current user
  const getVisibleColumns = (): TableColumn[] => {
    return computedContractColumns;
  };

  // Get columns that should actually be rendered (visible + available for user role)
  const getRenderedColumns = (): TableColumn[] => {
    return getVisibleColumns().filter((col) => isColumnVisible(col.key));
  };

  // Get searchable columns for API (exclude reassessment — it's a computed status, not a text column)
  const getSearchColumns = (): string[] => {
    return getRenderedColumns()
      .filter((col) => col.key !== 'reassessment')
      .map((col) => col.key);
  };

  // Render cell content based on column type
  const renderCellContent = (column: TableColumn, contract: Contract) => {
    switch (column.key) {
      case 'contract_number':
        return (
          <button className={styles.contractsTableLink} onClick={() => navigate(`/contracts/${contract.id}`)}>
            {contract.contract_number}
          </button>
        );

      case 'name':
        return (
          <div className={styles.contractsTableNameCell}>
            <div className={styles.contractsTableName}>{contract.name}</div>
            {contract.description && <div className={styles.contractsTableDescription}>{contract.description}</div>}
          </div>
        );

      case 'supplier':
        return permissions.canAccessSuppliers ? (
          <button className={styles.contractsTableLink} onClick={() => navigate(`/suppliers/${contract.supplier_id}`)}>
            {contract.supplier_name || `Supplier #${contract.supplier_id}`}
          </button>
        ) : (
          <span className={styles.contractsTableText}>
            {contract.supplier_name || `Supplier #${contract.supplier_id}`}
          </span>
        );

      case 'manager': {
        const managers = contract.contract_managers;
        return (
          <div className={styles.contractsTableManager}>
            {managers && managers.length > 0 ? (
              managers.map((m) => m.name).join(', ')
            ) : (
              <span className={styles.contractsTableUnassigned}>Unassigned</span>
            )}
          </div>
        );
      }

      case 'owner': {
        const owners = contract.contract_owners;
        return (
          <div className={styles.contractsTableManager}>
            {owners && owners.length > 0 ? (
              owners.map((o) => o.name).join(', ')
            ) : (
              <span className={styles.contractsTableUnassigned}>Unassigned</span>
            )}
          </div>
        );
      }

      case 'division':
        return (
          <div className={styles.contractsTableText}>
            {contract.division_name || contract.division || '-'}
          </div>
        );

      case 'scope':
        return (
          <div className={styles.scopeCell}>
            <span>{getScopeLabel(contract.assessment_scope) || '-'}</span>
            {contract.pending_assessment_scope && (
              <Badge variant="warning">Pending {getScopeLabel(contract.pending_assessment_scope)}</Badge>
            )}
          </div>
        );

      case 'initial_assessment_date':
        return <div className={styles.contractsTableDate}>{formatCalendarDate(contract.initial_assessment_date)}</div>;

      case 'last_assessment_date':
        return <div className={styles.contractsTableDate}>{formatCalendarDate(contract.last_assessment_date)}</div>;

      case 'target_date':
        return <div className={styles.contractsTableDate}>{formatCalendarDate(contract.target_date)}</div>;

      case 'value':
        return <div className={styles.contractsTableValue}>{formatCurrency(contract.amount)}</div>;

      case 'start_date':
        return <div className={styles.contractsTableDate}>{formatCalendarDate(contract.start_date)}</div>;

      case 'end_date':
        return <div className={styles.contractsTableDate}>{formatCalendarDate(contract.end_date)}</div>;

      case 'status':
        return <Badge variant={contractStatusToVariant(contract.status)}>{getContractStatusLabel(contract.status)}</Badge>;

      case 'reassessment':
        return <ReassessmentStatusCell reassessmentStatus={contract.reassessment_status} />;

      default: {
        const value = contract[column.key as keyof Contract];
        return <div className={styles.contractsTableText}>{typeof value === 'object' ? '-' : value || '-'}</div>;
      }
    }
  };

  // Update URL when filters change (using debounced search query)
  // Note: Pagination (page, limit) is handled by usePagination hook
  useEffect(() => {
    const params = new URLSearchParams(searchParams); // Preserve existing params (page, limit)

    if (debouncedSearchQuery) {
      params.set('search', debouncedSearchQuery);
    } else {
      params.delete('search');
    }

    if (statusFilter !== 'all') {
      params.set('status', statusFilter);
    } else {
      params.delete('status');
    }

    setSearchParams(params, { replace: true });
  }, [debouncedSearchQuery, statusFilter, searchParams, setSearchParams]);

  useEffect(() => {
    if (!showUnassignedManager && statusFilter === 'unassigned') {
      setStatusFilter('all');
    }
  }, [showUnassignedManager, statusFilter]);

  // Reset to page 1 when filters change
  useEffect(() => {
    resetToFirstPage();
  }, [debouncedSearchQuery, statusFilter, reassessmentFilter, debouncedColumnFilters, resetToFirstPage]);

  // Sorting with hook
  const { sortConfig, handleSort, isSorted } = useTableSort({
    columnMapping: {
      contract_number: 'contract_number',
      name: 'name',
      supplier: 'supplier',
      manager: 'manager_names',
      contract_type: 'contract_type',
      division: 'division',
      cost_center: 'cost_center',
      tier: 'tier',
      value: 'amount',
      start_date: 'start_date',
      end_date: 'end_date',
      status: 'status',
      scope: 'assessment_scope',
      initial_assessment_date: 'initial_assessment_date',
      last_assessment_date: 'last_assessment_date',
      target_date: 'target_date'
    },
    defaultSort: { field: 'contract_number', direction: 'ASC' },
    onPageReset: () => setCurrentPage(1)
  });

  // Fetch contracts with filters using hook
  const {
    contracts,
    isLoading,
    error,
    isFetching,
    filterCounts,
    reassessmentCounts,
    totalItems: totalContracts,
    totalPages,
    refreshData
  } = useContractPageData({
    queryKey: [
      'contracts',
      debouncedSearchQuery,
      statusFilter,
      reassessmentFilter,
      currentPage,
      pageSize,
      visibleColumns,
      debouncedColumnFilters,
      sortConfig
    ],
    pageSize, // Pass pageSize for correct totalPages calculation
    queryFn: () => {
      const filters = {
        searchTerm: debouncedSearchQuery || undefined,
        searchColumns: getSearchColumns(),
        status: statusFilter !== 'all' && statusFilter !== 'unassigned' ? statusFilter : undefined,
        unassigned: statusFilter === 'unassigned' ? true : undefined,
        columnFilters: getColumnFiltersParam() || undefined,
        sortBy: sortConfig.field,
        order: sortConfig.direction,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        reassessmentStatus: reassessmentFilter as 'Reassessment Due' | 'Ready for Scheduling' | undefined
      };
      return contractService.getContracts(filters);
    }
  });

  // Columns with dynamic filterNode for the reassessment inline filter, plus
  // the option sets for the selection filters (fetched once per session).
  const computedContractColumns = useMemo(
    (): TableColumn[] =>
      contractColumns.map((col) => {
        if (col.filterType === 'select') {
          const optionsByColumn: Record<string, typeof filterOptions.status> = {
            status: filterOptions.status,
            scope: filterOptions.scope,
            division: filterOptions.division,
            tier: filterOptions.tier,
            contract_type: filterOptions.contractType
          };
          return { ...col, filterOptions: optionsByColumn[col.key] ?? [] };
        }

        if (col.key !== 'reassessment') return col;
        return {
          ...col,
          filterNode: (
            <select
              className={styles.reassessmentFilterSelect}
              value={reassessmentFilter || ''}
              onChange={(e) => {
                setReassessmentFilter(e.target.value || undefined);
                resetToFirstPage();
              }}
            >
              <option value="">All</option>
              <option value="Reassessment Due">
                Reassessment Due ({reassessmentCounts.reassessmentDue})
              </option>
              <option value="Ready for Scheduling">
                Ready for Scheduling ({reassessmentCounts.readyForScheduling})
              </option>
            </select>
          )
        };
      }),
    [reassessmentFilter, reassessmentCounts, resetToFirstPage, filterOptions]
  );

  // Format currency
  const formatCurrency = (value?: number) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  // Handle filter change
  const handleFilterChange = (filter: string) => {
    setStatusFilter(filter);
    setCurrentPage(1);
  };

  // Export mutation - fetches the full filtered dataset (all pages), not just the current page
  const exportMutation = useMutation({
    mutationFn: () =>
      contractService.getContractsForExport({
        searchTerm: debouncedSearchQuery || undefined,
        searchColumns: getSearchColumns(),
        status: statusFilter !== 'all' && statusFilter !== 'unassigned' ? statusFilter : undefined,
        unassigned: statusFilter === 'unassigned' ? true : undefined,
        columnFilters: getColumnFiltersParam() || undefined,
        sortBy: sortConfig.field,
        order: sortConfig.direction,
        reassessmentStatus: reassessmentFilter as 'Reassessment Due' | 'Ready for Scheduling' | undefined
      }),
    onSuccess: (response) => {
      exportContracts(response.data, Array.from(visibleColumns));
    },
    onError: (err: unknown) => {
      console.error('Failed to export contracts:', err);
      showToast.error(getApiErrorMessage(err, 'Failed to export contracts. Please try again.'));
    }
  });

  // Handle export
  const handleExport = () => {
    exportMutation.mutate();
  };

  if (error) {
    return (
      <PageLayout>
        <ErrorState message="Error loading contracts. Please try again." />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <ListPageHeader
        icon={<FileText size={24} />}
        title={permissions.isContractRole ? 'My Contracts' : 'Contracts'}
        count={filterCounts.all || 0}
        searchValue={searchQuery}
        onSearchChange={handleSearch}
        searchPlaceholder="Search contracts, numbers, owners, managers..."
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExport}
              disabled={exportMutation.isPending || !contracts || contracts.length === 0}
            >
              <Download size={16} />
              {exportMutation.isPending ? 'Exporting...' : 'Export List'}
            </Button>

            <ColumnSelector
              columns={getVisibleColumns()}
              visibleColumns={Array.from(visibleColumns)}
              onToggleColumn={toggleColumn}
              dataTestId="column-selector"
            />
          </>
        }
        additionalContent={
          showPendingScopeLink || permissions.canManageContracts ? (
            <>
              {showPendingScopeLink && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/conflicts/pending-scope')}
                  dataTestId="pending-scope-changes-link"
                >
                  <Flag size={16} />
                  {`Pending Scope Changes (${pendingScopeCount})`}
                </Button>
              )}
              {permissions.canManageContracts && (
                <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
                  <Plus size={16} />
                  New Contract
                </Button>
              )}
            </>
          ) : undefined
        }
      />

      {/* Professional Filters */}
      <FilterButtons
        filters={statusFilters}
        activeFilter={statusFilter}
        onFilterChange={handleFilterChange}
        filterCounts={filterCounts}
        dataTestId="contracts-filter"
      />

      {/* Loading overlay when fetching data */}
      {isFetching && !isLoading && <LoadingOverlay variant="page" />}

      {/* Professional Table */}
      <div className={styles.contractsTable}>
        <RZTable
          data={contracts}
          columns={getRenderedColumns()}
          getRowKey={(contract) => contract.id}
          renderCell={renderCellContent}
          sorting={{
            sortConfig,
            onSort: handleSort,
            isSorted
          }}
          columnFilters={{
            values: columnFilterValues,
            onChange: setColumnFilter,
            onClearAll: clearAllFilters,
            hasActiveFilters: hasAnyActiveFilter
          }}
          pagination={{
            currentPage,
            totalPages,
            totalItems: totalContracts,
            pageSize,
            onPageChange: setCurrentPage,
            onPageSizeChange: setPageSize,
            itemName: 'contracts',
            pageSizeOptions: [25, 50, 100]
          }}
          state={{
            isLoading,
            isEmpty: contracts.length === 0,
            loadingMessage: 'Loading contracts...'
          }}
          emptyState={
            <RZTable.Empty
              icon={<FileText size={48} />}
              title="No contracts found"
              description="Try changing filters or search criteria"
            >
              {permissions.canManageContracts && (
                <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                  <Plus size={16} />
                  Create Your First Contract
                </Button>
              )}
            </RZTable.Empty>
          }
        />
      </div>

      {/* Create Contract Modal */}
      <ContractFormModal
        mode="create"
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          refreshData();
          resetToFirstPage();
        }}
      />
    </PageLayout>
  );
};

export default ContractsPage;
